<template>
  <div>
    <v-navigation-drawer
      v-model="info.activated"
      :width="$isMobile ? '100%' : '50%'"
      right
      fixed
      :permanent="info.activated"
      hide-overlay
      class="z-index--100">
      <div class="ma-3">
        <div class="d-flex">
          <v-btn
            dark
            fab
            x-small
            outlined
            color="primary"
            class="mr-3"
            @click="info.activated=false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <div class="subtitle-1 primary--text">
            Información de {{ info.item }}
          </div>
        </div>
        <div>
          <div
            v-if="info.loading"
            class="d-flex justify-center align-center">
            <v-progress-circular
              indeterminate
              color="primary"
            />
          </div>
          <div
            v-else>
            <div v-if="info.type === 'input'">
              <div
                v-if="info.data.recipes && info.data.recipes.length"
                class="mb-4">
                <div
                  class="py-3 primary--text font-weight-bold">
                  Uso en recetas:
                </div>
                <v-card>
                  <v-data-table
                    :items="info.data.recipes"
                    :headers="[
                      { text: this.$i18n.t('recipes.headers.name'), value: 'name', sortable: true },
                      { text: this.$i18n.t('recipes.headers.quantity'), value: 'ingredient', sortable: false },
                      { text: this.$i18n.t('recipes.headers.avgWeeklySale'), value: 'weeklySale', sortable: false }
                    ]"
                    dense>
                    <template v-slot:item.ingredient="{ item }">
                      <span>
                        {{ item.ingredient ? item.ingredient.quantity : '' }}
                      </span>
                      <span class="caption grey--text">
                        {{ item.ingredient ? item.ingredient.unit.name : '' }}
                      </span>
                    </template>
                    <template v-slot:item.weeklySale="{ item }">
                      {{ item.weeklySale }}
                    </template>
                  </v-data-table>
                </v-card>
              </div>
            </div>
            <div v-if="info.type === 'quantity'">
              <div
                v-if="info.data.subrecipes && info.data.subrecipes.length"
                class="mb-4">
                <div
                  class="py-3 primary--text font-weight-bold">
                  Uso en inventario:
                </div>
                <v-card>
                  <v-data-table
                    :items="info.data.subrecipes"
                    :headers="[
                      { text: this.$i18n.t('recipes.headers.name'), value: 'name', sortable: true },
                      { text: this.$i18n.t('recipes.headers.quantityCalculated'), value: 'quantityCalculated', sortable: false },
                      { text: `${this.$i18n.t('recipes.headers.quantityInWarehouse')} ${info.item} usada`, value: 'quantityInWarehouse', sortable: false }
                    ]"
                    dense>
                    <template v-slot:item.quantityCalculated="{ item }">
                      <span>
                        {{ item.quantityCalculated | toFixNumber(2) }}
                      </span>
                      <span class="caption grey--text">
                        {{ item.unit.name }}
                      </span>
                    </template>
                    <template v-slot:item.quantityInWarehouse="{ item }">
                      <span>
                        {{ item.quantityInWarehouse | toFixNumber(2) }}
                      </span>
                      <span class="caption grey--text">
                        {{ info.data.displayUnit.name }}
                      </span>
                      <span
                        v-if="info.data.contentUnit.name !== info.data.displayUnit.name"
                        class="caption grey--text">
                        ({{ info.data.contentQuantity }} {{ info.data.contentUnit.name }})
                      </span>
                    </template>
                  </v-data-table>
                </v-card>
              </div>
              <div v-else>
                <div
                  class="py-3 primary--text font-weight-bold">
                  No hay subrecetas en almacén que usen este producto
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <div class="title primary--text">{{ section }}</div>
      <v-spacer />
      <div class="d-flex justify-end align-center mr-3">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Busca productos por nombre"
          solo
          hide-details
          rounded
          flat
          dense/>
      </div>
    </div>

    <BalanceDetail :total="total">
      <v-col class="d-flex justify-space-between">
        <div class="subtitle-2">
          Compra total:
        </div>
        <div class="'primary--text'">
          {{ total | toFixNumber(2) | prefix('$') | numberWithComma }}
        </div>
      </v-col>
    </BalanceDetail>

    <div
      v-if="!validTaxInformation"
      class="pt-3 px-3">
      <v-alert type="warning">
        <div class="d-flex justify-space-between">
          Necesitas llenar los datos fiscales de tu restaurante para poder realizar una compra.
          <v-btn
            rounded
            outlined
            small
            to="/settings/restaurant">
            Ir a datos fiscales.
          </v-btn>
        </div>
      </v-alert>
    </div>

    <div
      v-if="loadedData.length && isLoaded"
      class="pa-3 d-flex justify-space-between flex-md-row flex-column">
      <div>
        <v-btn-toggle
          v-model="favorites"
          rounded
          mandatory>
          <v-btn
            small
            class="white--text"
            :outlined="favorites !== 0"
            color="primary">
            Todos
          </v-btn>
          <v-btn
            class="white--text"
            small
            :outlined="favorites !== 1"
            color="primary">
            Favoritos
          </v-btn>
        </v-btn-toggle>
      </div>

      <div>
        <v-btn
          class="mt-3 mt-md-0 mr-md-3"
          rounded
          small
          color="secondary"
          target="_blank"
          :href="exportCSV">
          Descargar lista como CSV
        </v-btn>
        <v-btn
          v-if="smartPurchasePermissions['create-purchase-order-by-stock']"
          :disabled="!(selectedProducts.length && validTaxInformation)"
          class="mt-3 mt-md-0 mr-md-3"
          rounded
          small
          color="primary"
          @click="generateOrder">
          Generar orden de compra
        </v-btn>
      </div>
    </div>

    <v-flex xs12>
      <v-form
        v-if="isLoaded && loadedData.length"
        ref="purchaseOrder"
        v-model="isValid">
        <v-data-table
          v-model="selectedProducts"
          :loading-text="table.title"
          :server-items-length="table.total"
          :loading="table.loading"
          :headers="headers"
          :items="table.data"
          :options="table.options"
          :sort-by="sortAttrName"
          show-select
          selectable-key="id"
          @update:options="updateTable"
          @update:sort-by="sortByTable"
          @update:sort-desc="sortDescTable"
          :footer-props="table.footer"
          class="elevation-1 d-print fixed-header">
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-items-center" style="max-width:200px">
              <a
                v-if="$is() !== 'stockmanager'"
                href="#"
                @click.prevent="openInfo(item, 'input')">
                {{ item.name }}
              </a>
              <div v-else>
                {{ item.name }}
                <span v-if="item.choice && item.choice.description" class="grey--text">({{ item.choice.description }})</span>
              </div>
            </div>
            <div v-if="item.choice && item.choice.description" class="caption grey--text text-uppercase">
              {{ item.choice.description }}
            </div>
          </template>
          <template v-slot:item.choice.provider="{ item }">
            <div style="max-width:200px">
              <v-select
                v-model="item.choice"
                :items="item.providers"
                item-text="provider.name"
                item-value="id"
                no-data-text="No hay más opciones"
                hide-details
                dense
                outlined
                rounded
                hide-selected
                hide-no-data
                return-object
                @change="calculateTotal">
                <template v-slot:item="{ item }">
                  <div class="caption">
                    {{ item.provider.name }}
                    <span class="grey--text">
                      {{ item.amount | toFixNumber(2) | prefix('$') }} | {{ item.displayQuantity }} {{ item.displayUnit.name || '' }}
                    </span>
                  </div>
                </template>
              </v-select>
            </div>
          </template>
          <template v-slot:item.choice.amount="{ item }">
            <div
              v-if="item.choice"
              class="text-right">
              <div>
                {{ item.choice.amount | toFixNumber(2) | prefix('$') | numberWithComma }}
              </div>
              <div class="caption grey--text">
                1/{{ item.unit.code }}
              </div>
            </div>
            <div
              v-else
              class="text-right">
              <div>
                {{ item.product.cost | toFixNumber(2) | prefix('$') | numberWithComma }}
              </div>
              <div class="caption grey--text">
                1/{{ item.unit.code }}
              </div>
            </div>
          </template>
          <template
            v-slot:item.quantityCalculated="{ item }"
            v-if="['manager', 'stockmanager', 'buyer'].includes($is())">
            <div class="text-right" style="max-width:250px">
              <div>
                {{ item.quantityCalculated | toFixNumber(3) | numberWithComma }}
              </div>
              <div class="caption grey--text">
                {{ item.choice.displayUnit.name }}
              </div>
            </div>
          </template>
          <template v-slot:item.quantity="{ item }">
            <div class="d-flex pt-3 align-center" style="min-width:150px">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-on="on"
                    small
                    class="mr-2"
                    @click="openInfo(item, 'quantity')">
                    mdi-information
                  </v-icon>
                </template>
                <span>Información de la sugerencia</span>
              </v-tooltip>
              <div>
                <v-text-field
                  v-model.number="item.quantity"
                  outlined
                  rounded
                  dense
                  hide-details
                  :rules="rules.quantity"
                  type="number"
                  @input="calculateTotal">
                </v-text-field>
              </div>
            </div>
            <div class="caption pb-3 text-right grey--text">
              {{ item.choice.displayUnit.name }}
            </div>
          </template>
        </v-data-table>
      </v-form>

      <div
        v-else-if="isLoaded"
        class="d-flex justify-center mt-6">
        <v-row
          align="center"
          justify="center">
          <v-icon
            size="150"
            color="primary">
            mdi-cogs
          </v-icon>
          <v-col cols="4">
            <div class="grey--text">
              <div
                class="title mb-3 font-weight-bold">
                Aún no tienes configurado tu estimación de ventas semanales.
              </div>
              <div class="subtitle-2">
                <div class="mb-3">
                  Ingresa la estimación de ventas de tus platillos para poder usar el sistema de pedidos inteligente.
                </div>
                <div>
                  <router-link class="primary--text" :to="{ name: 'Dishes' }">Ir a configurar la estimación de ventas semanales</router-link>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <div class="d-flex justify-center" v-else>
        <v-progress-circular color="primary" indeterminate size="64" />
      </div>
    </v-flex>

    <v-dialog
      @keydown.esc="alert=false"
      v-model="alert"
      max-width="450">
      <v-card
        color="blue-grey">
        <v-card-title/>
        <v-card-text>
          <div class="d-flex white--text">
            <div class="mr-2">
              <v-icon color="white">mdi-alert</v-icon>
            </div>
            <div class="title">
              {{ alertMessage }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            color="white"
            text
            @click="alert = false">
            Ok
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Order from '@/models/Order'
import Restaurant from '@/models/Restaurant'
import BalanceDetail from '@/components/elements/BalanceDetail'
import _ from 'lodash'

export default {
  components: {
    BalanceDetail
  },
  data () {
    let orderModel = new Order()
    let restaurantModel = new Restaurant()

    return {
      orderModel,
      restaurantModel,
      detail: {
        fees: {
          purchaseOrder: 3
        }
      },
      isValid: false,
      resetDialog: false,
      overlay: false,
      alert: false,
      alertMessage: '',
      section: this.$i18n.t('intelligentPurchase.section'),
      sortAttrName: 'name',
      attrNamesSorteable: ['name', 'choice.provider.name', 'area.name'],
      loadedData: [],
      table: {
        title: this.$i18n.t('intelligentPurchase.loading'),
        loading: true,
        headers: [
          { text: this.$i18n.t('intelligentPurchase.headers.name'), value: 'name', sortable: true },
          { text: this.$i18n.t('intelligentPurchase.headers.category'), value: 'product.category.name' },
          { text: this.$i18n.t('intelligentPurchase.headers.provider'), value: 'choice.provider' },
          { text: this.$i18n.t(`intelligentPurchase.headers.quantityCalculated.${this.$is()}`), value: 'quantityCalculated', sortable: false, align: 'right' },
          { text: this.$i18n.t(`intelligentPurchase.headers.purchaseOrder`), value: 'quantity', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          itemsPerPage: 1000,
          page: 1
        },
        footer: {
          'items-per-page-options': [5, 50, 100, 200]
        }
      },
      info: {
        type: '',
        activated: false,
        loading: false,
        item: null,
        data: {
          recipes: null,
          movements: null,
          subrecipes: null
        }
      },
      categories: [],
      providers: [],
      category: null,
      provider: null,
      search: '',
      rules: {
        numberValid: [
          v => (v !== '' && !isNaN(v * 1)) || this.$t('validations.numberValid')
        ],
        quantity: [v => !!v || this.$t('validations.quantity')]
      },
      balance: null,
      total: 0,
      selectedProducts: [],
      isLoaded: false,
      favorites: 0,
      prospection: null
    }
  },
  computed: {
    exportCSV () {
      return `${process.env.VUE_APP_API_URL}/v1/prospections/${this.prospection}/download`
    },
    headers () {
      const allowedHeaders = this.table.headers
      return allowedHeaders
    },
    inventoryAt () {
      if (this.table.data.length > 0) {
        return this.table.data[0].updatedQuantityAvailableAt || null
      }
      return null
    },
    restaurant () {
      return this.$store.state.currentRestaurant.data
    },
    idArea: function () {
      return this.$store.state.area.id || null
    },
    units: function () {
      return this.$store.state.login.units.data
    },
    validTaxInformation: function () {
      if (this.$store.state.currentRestaurant.taxInformation.data) {
        return (this.$store.state.currentRestaurant.taxInformation.data.taxId && this.$store.state.currentRestaurant.taxInformation.data.taxName)
      }
      return false
    },
    smartPurchasePermissions () {
      return this.$store.state.login.permissions['purchase-orders'].components['smart-purchase-orders-form'].actions
    }
  },
  watch: {
    'search': async function (val) {
      const params = {
        limit: this.table.options.itemsPerPage,
        area: this.$store.state.area.id,
        skip: (this.table.options.itemsPerPage * (this.table.page - 1)),
        sort: 'name',
        subrecipe: false
      }
      if (val.length >= 3) {
        try {
          params.name = new RegExp(val, 'i')
          let content = this.loadedData
          content = content.filter(x => params.name.test(x.name))
          this.table.data = content
          this.table.total = content.length
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
      }
    },
    selectedProducts: function (val) {
      if (val) {
        this.calculateTotal()
      }
    }
  },
  async mounted () {
    window.addEventListener('keydown', this.keyDownHandler)
    await this.getCategories()
    await this.load({
      limit: -1,
      skip: 0,
      sort: 'name',
      area: this.$store.state.area.id,
      only: 'Recipe'
    })
    await this.detailRestaurant()
  },
  destroyed () {
    window.removeEventListener('keydown', this.keyDownHandler)
  },
  methods: {
    keyDownHandler (event) {
      if (event.code === 'Escape') this.info.activated = false
    },
    openAmountWithheldDetail () {
      this.$refs.amountWithheld.open()
    },
    async detailRestaurant () {
      this.restaurantModel.set(this.restaurant)
      const res = await this.restaurantModel.detail('fees')
      this.detail = res.response.data
    },
    async openInfo (item, type) {
      this.info.loading = true
      this.info.activated = true
      this.info.type = type
      this.info.item = item.name
      if (type === 'input') {
        await this.getInputInfo(item)
      }
      if (type === 'quantity') {
        this.info.data.subrecipes = item.subrecipes && item.subrecipes.length
          ? item.subrecipes.filter(subrecipe => subrecipe.quantityInWarehouse > 0)
          : []
        this.info.data.displayUnit = item.choice.displayUnit
        this.info.data.contentUnit = item.choice.contentUnit
        this.info.data.contentQuantity = item.choice.contentQuantity
      }
      this.info.loading = false
    },
    async getInputInfo (item) {
      try {
        const data = await this.$api.get('/v1/inputs/info', { product: item.product.id })
        this.info.data = data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    },
    updateTable: async function (data) {
      const { sortBy, sortDesc } = data
      const start = data.itemsPerPage * (data.page - 1)
      const end = data.itemsPerPage * data.page
      const sortParam = (sortDesc > 0 ? '' : '-') + sortBy
      this.table.data = _.sortBy(this.loadedData, sortParam).slice(start, end)
    },
    async load (params = null) {
      params = params || {
        area: this.$store.state.area.id
      }
      this.table.loading = true
      this.overlay = true
      try {
        const response = await this.$api.get('/v1/prospections', params)

        this.prospection = response._id

        this.loadedData = response.products
          .filter(product => product.quantity > 0)
          .map(product => {
            const quantityCalculated = product.quantity
            const choices = product.product.prices.map(choice => {
              const unit = this.units.find(unit => unit.id === choice.displayUnit)
              return Object.assign(
                {},
                choice,
                {
                  displayUnit: unit
                }
              )
            })
            return Object.assign(
              product,
              {
                quantity: Math.ceil(product.quantity),
                providers: choices,
                quantityCalculated: quantityCalculated
              }
            )
          })
        this.table.data = this.loadedData

        this.providers = _.uniqBy(this.loadedData.map(item => item.choice.provider), 'id').sort((a, b) => {
          if (a.name > b.name) return 1
          if (a.name < b.name) return -1
          return 0
        })
        this.providers.unshift({ id: null, name: 'Todos' })

        await this.updateTable({
          sortBy: 'name',
          sortDesc: 1,
          itemsPerPage: this.table.options.itemsPerPage,
          page: this.table.options.page
        })
        this.table.total = this.loadedData.length
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.isLoaded = true
      this.table.loading = false
      this.overlay = false
    },
    async getCategories () {
      const { data } = await this.$api.get(`/v1/categories`, { sort: 'name' })
      data.unshift({ id: null, name: 'Todas' })
      this.categories = data
    },
    async sortDescTable (data) {
      if (!data) return
      this.loadedData = this.loadedData.reverse()
      await this.updateTable({
        sortBy: this.sortAttrName,
        sortDesc: -1,
        itemsPerPage: this.table.options.itemsPerPage,
        page: this.table.options.page
      })
    },
    async sortByTable (data) {
      if (!data && !this.attrNamesSorteable.includes(data)) return
      const floatColumns = [ 'difference' ]
      this.loadedData = this.loadedData.sort((a, b) => {
        a = _.get(a, data)
        b = _.get(b, data)
        if (floatColumns.includes(data)) {
          a = parseFloat(a)
          b = parseFloat(b)
        }
        if (a > b) return 1
        if (a < b) return -1
        return 0
      })
      this.sortAttrName = data
      await this.updateTable({
        sortBy: data,
        sortDesc: 1,
        itemsPerPage: this.table.options.itemsPerPage,
        page: this.table.options.page,
        area: this.$store.state.area.id
      })
    },
    async generateOrder () {
      this.overlay = true
      try {
        const payload = {
          area: this.idArea,
          paymentMethod: 'balance',
          choices: this.selectedProducts.map(item => ({ id: item.choice.id, quantity: item.quantity }))
        }
        await this.$api.post('/v1/orders', payload)
        await this.$api.post(`/v1/prospections/${this.prospection}/clear`)
        this.$toasted.show('La orden se creó correctamente', { duration: 3000 })
        this.$router.push({ name: 'PurchaseOrders' })
      } catch (error) {
        this.alertMessage = error.message
        this.alert = true
      }
      this.overlay = false
    },
    calculateTotal () {
      const amount = this.selectedProducts.map(product => {
        if (product.choice.amount && product.quantity) {
          return this.calculatePriceProductWithTaxes(product.choice) * Math.ceil(product.quantity)
        } else {
          return 0
        }
      })
      const subtotal = amount.reduce((acc, num) => { return acc + num }, 0)
      this.total = subtotal
    },
    calculatePriceProductWithTaxes (product) {
      let priceWithTaxes = product.amount
      let totalTaxes = 0
      const ieps = product.taxes.find(tax => tax.satCode === '003')
      const iva = product.taxes.find(tax => tax.satCode === '002')
      const otherTaxes = product.taxes
        .filter(tax => !['003', '002'].includes(tax.satCode))

      if (ieps) {
        priceWithTaxes = priceWithTaxes + ieps.base
      }

      totalTaxes = otherTaxes.reduce((acc, num) => {
        let taxApplied
        if (num.isQuota) {
          taxApplied = num.base
        } else {
          taxApplied = (num.base / 100) * product.amount
        }
        acc += taxApplied
        return acc
      }, 0)

      if (iva) {
        priceWithTaxes = priceWithTaxes + (priceWithTaxes * (iva.base / 100))
      }

      priceWithTaxes = totalTaxes + priceWithTaxes

      return priceWithTaxes
    }
  }
}
</script>
